import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { produce } from 'immer';
import { ReactNode } from 'react';

import { DEVTOOLS_OPTIONS } from '@/modules/debug/constants/zustand';
import { PanelDisplayMode, Panel_Display_Mode } from '@/modules/floorplanDesign/types';
import { ToolId } from '@modules/toolbar/types';
import { ModalId } from '@/modules/common/components/ModalManager/constants';
import { SizeShort } from '@/modules/common/types/general';

import { FloorplanInteractionMode, LoadingType, Module } from '../types';

export interface CanvasOverlayRequest {
  message: ReactNode;
  onCancel?: () => void;
}

export type AppState = {
  selectedIds: string[];
  panelModeState: PanelDisplayMode;
  /** * @description
   * Use this to set which modal is currently active. This can be used to prevent interactions with the canvas when a modal is open.
   */
  activeModalId: ModalId | null;
  /**
   * @description
   * Use this to prevent having multiple popovers open at the same time.
   */
  activePopoverIdState: null | string;
  /**
   * @description
   * Selected tool identifier
   */
  selectedToolId: ToolId;
  viewportSize: SizeShort;
  mode: FloorplanInteractionMode;
  activeModule: Module;
  clickableElementIds: string[];
  disabledElementIds: string[];
  /**
   * @description flag which can be used to momentarily disable / enable autosaving of the currently loaded revision
   */
  autosaveEnabled: boolean;
  canvasOverlayRequest: CanvasOverlayRequest | null;
  showLoadContainer: boolean;
  loadContainerType: LoadingType;
};

const INITIAL_STATE: AppState = {
  selectedIds: [],
  activeModalId: null,
  panelModeState: Panel_Display_Mode.NONE,
  activePopoverIdState: null,
  selectedToolId: 'cursor',
  viewportSize: { w: 0, h: 0 },
  mode: 'EDITABLE',
  activeModule: 'canvas_setup',
  clickableElementIds: [],
  disabledElementIds: [],
  autosaveEnabled: false,
  canvasOverlayRequest: null,
  showLoadContainer: true,
  loadContainerType: 'TRANSPARENT',
};

type AppActions = {
  selection: {
    add(ids: AppState['selectedIds']): void;
    remove(ids: AppState['selectedIds']): void;
    set(ids: AppState['selectedIds']): void;
    clear(): void;
  };
  panelMode: {
    set(value: PanelDisplayMode): void;
    get(): PanelDisplayMode;
  };
  modal: {
    setActiveModalId(value: ModalId | null): void;
  };
  popover: {
    setActiveId(value: null | string): void;
  };
  toolbar: {
    setSelectedToolId(tool: ToolId): void;
  };
  viewport: {
    setViewport(size: SizeShort): void;
  };
  module: {
    setModule(module: Module): void;
  };
  floorplanInteractionMode: {
    setMode(mode: FloorplanInteractionMode): void;
  };
  enabling: {
    getClickableElementIds(): string[];
    setClickableElementIds(ids: string[]): void;
    getDisabledElementIds(): string[];
    setDisabledElementIds(ids: string[]): void;
  };
  canvasOverlay: {
    /** Last writer wins. Pass `null` to clear. */
    set(request: CanvasOverlayRequest | null): void;
  };
  loadContainer: {
    setShowLoadContainer(show: boolean): void;
    setLoadContainerType(type: LoadingType): void;
  };

  transact(updater: (draft: AppState) => void): void;
  setAutosaveEnabled(enabled: boolean): void;
  reset(): void;
};

export const useAppStore = create<AppState & AppActions>()(
  devtools(
    (set, get) => ({
      ...INITIAL_STATE,

      selection: {
        add(ids) {
          const newIds = new Set(get().selectedIds);
          ids.forEach((id) => newIds.add(id));

          set(
            {
              selectedIds: [...newIds],
            },
            undefined,
            {
              type: 'selection/add',
            },
          );
        },

        remove(ids) {
          const newIds = new Set(get().selectedIds);
          ids.forEach((id) => newIds.delete(id));

          set(
            {
              selectedIds: [...newIds],
            },
            undefined,
            {
              type: 'selection/remove',
            },
          );
        },

        set(ids) {
          set(
            {
              selectedIds: ids,
            },
            undefined,
            {
              type: 'selection/set',
            },
          );
        },

        clear() {
          set(
            {
              selectedIds: [],
            },
            undefined,
            {
              type: 'selection/clear',
            },
          );
        },
      },

      panelMode: {
        set(value) {
          set(
            {
              panelModeState: value,
            },
            undefined,
            {
              type: 'panelModeState/set',
            },
          );
        },

        get() {
          return get().panelModeState;
        },
      },

      modal: {
        setActiveModalId(activeModalId) {
          set(
            {
              activeModalId,
            },
            undefined,
            {
              type: 'modal/setActiveModalId',
            },
          );
        },
      },

      popover: {
        setActiveId(activePopoverIdState) {
          set(
            {
              activePopoverIdState,
            },
            undefined,
            {
              type: 'popover/setActiveId',
            },
          );
        },
      },

      toolbar: {
        setSelectedToolId(selectedToolId: ToolId) {
          set(
            {
              selectedToolId,
            },
            undefined,
            {
              type: 'toolbar/setSelectedTool',
            },
          );
        },
      },

      viewport: {
        setViewport(size: SizeShort) {
          set(
            {
              viewportSize: size,
            },
            undefined,
            {
              type: 'viewport/setSize',
            },
          );
        },
      },

      module: {
        setModule(module: Module) {
          set(
            {
              activeModule: module,
            },
            undefined,
            {
              type: 'module/setModule',
            },
          );
        },
      },

      floorplanInteractionMode: {
        setMode(mode: FloorplanInteractionMode) {
          set(
            {
              mode,
            },
            undefined,
            {
              type: 'floorplanInteractionMode/setMode',
            },
          );
        },
      },

      enabling: {
        getClickableElementIds() {
          return get().clickableElementIds;
        },

        setClickableElementIds(ids) {
          set(
            {
              clickableElementIds: ids,
            },
            undefined,
            {
              type: 'enabling/setEnabledElementIds',
              ...DEVTOOLS_OPTIONS,
            },
          );
        },

        getDisabledElementIds() {
          return get().disabledElementIds;
        },

        setDisabledElementIds(ids) {
          set(
            {
              disabledElementIds: ids,
            },
            undefined,
            {
              type: 'enabling/setDisabledElementIds',
              ...DEVTOOLS_OPTIONS,
            },
          );
        },
      },

      canvasOverlay: {
        set(canvasOverlayRequest) {
          set({ canvasOverlayRequest }, undefined, {
            type: 'canvasOverlay/set',
            ...DEVTOOLS_OPTIONS,
          });
        },
      },

      loadContainer: {
        setShowLoadContainer(show: boolean) {
          set(
            {
              showLoadContainer: show,
            },
            undefined,
            {
              type: 'loadContainer/setShowLoadContainer',
              ...DEVTOOLS_OPTIONS,
            },
          );
        },

        setLoadContainerType(type: LoadingType) {
          set(
            {
              loadContainerType: type,
            },
            undefined,
            {
              type: 'loadContainer/setLoadContainerType',
              ...DEVTOOLS_OPTIONS,
            },
          );
        },
      },

      setAutosaveEnabled(value) {
        set(
          {
            autosaveEnabled: value,
          },
          undefined,
          {
            type: 'autosaveEnabled/set',
            ...DEVTOOLS_OPTIONS,
          },
        );
      },

      transact(updater) {
        set((state) => produce(state, (draft) => updater(draft)), undefined, {
          type: 'transact',
        });
      },

      reset() {
        set(INITIAL_STATE, undefined, {
          type: 'reset',
        });
      },
    }),
    { store: 'appStore', ...DEVTOOLS_OPTIONS },
  ),
);
